import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where, orderBy, updateDoc, doc } from "firebase/firestore";
import "./UpgradeRequests.css";
import { useAccountType } from "../../hooks/useAccountType";

export default function UpgradeRequests() {
  const { plan: role } = useAccountType();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending, all, completed
  const [charging, setCharging] = useState({});

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let q;
      if (filter === "all") {
        q = query(
          collection(db, "upgrade_requests"),
          orderBy("createdAt", "desc")
        );
      } else {
        q = query(
          collection(db, "upgrade_requests"),
          where("status", "==", filter),
          orderBy("createdAt", "desc")
        );
      }

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setRequests(data);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    const note = window.prompt("Add approval note (optional)", "");
    setCharging(prev => ({ ...prev, [request.id]: true }));

    try {
      const targetUserId = request.userId || request.user_id || request.uid;
      if (!targetUserId) {
        throw new Error("Missing user id on upgrade request.");
      }

      const response = await fetch(
        "https://wealth-builder-backend-939575270287.us-east1.run.app/api/v1/subscriptions/sync-plan",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            plan: request.requestedLevel,
            user_id: targetUserId,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || "Failed to sync approved plan.");
      }

      await updateDoc(doc(db, "upgrade_requests", request.id), {
        status: "approved",
        approvalNotes: note || "",
        updatedAt: new Date().toISOString(),
      });

      alert(`Approved upgrade to ${request.requestedLevel} for ${request.userEmail}`);
      fetchRequests();
    } catch (error) {
      console.error("Error approving:", error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setCharging(prev => ({ ...prev, [request.id]: false }));
    }
  };

  const handleReject = async (request) => {
    const reason = window.prompt("Reason for rejection (optional)", "");
    setCharging(prev => ({ ...prev, [request.id]: true }));
    try {
      await updateDoc(doc(db, "upgrade_requests", request.id), {
        status: "rejected",
        rejectionReason: reason || "",
        updatedAt: new Date().toISOString(),
      });
      alert(`Request rejected for ${request.userEmail}`);
      fetchRequests();
    } catch (error) {
      console.error("Error rejecting request:", error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setCharging(prev => ({ ...prev, [request.id]: false }));
    }
  };

  if (role !== "Admin") {
    return (
      <div className="upgrade-requests-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Only administrators can view upgrade requests.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="upgrade-requests-page">
      <div className="upgrade-requests-header">
        <h1>Account Upgrade Requests</h1>
        <p className="subtitle">Review and process upgrade requests with saved payment methods</p>
      </div>

      <div className="filter-tabs">
        <button
          className={`filter-tab ${filter === "pending" ? "active" : ""}`}
          onClick={() => setFilter("pending")}
        >
          Pending
        </button>
        <button
          className={`filter-tab ${filter === "completed" ? "active" : ""}`}
          onClick={() => setFilter("completed")}
        >
          Completed
        </button>
        <button
          className={`filter-tab ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          All
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          <p>No {filter !== "all" ? filter : ""} requests found</p>
        </div>
      ) : (
        <div className="requests-table-container">
          <table className="requests-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Current</th>
                <th>Requested</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(request => (
                <tr key={request.id} className={`status-${request.status}`}>
                  <td className="name-cell">{request.name}</td>
                  <td className="email-cell">{request.userEmail}</td>
                  <td className="mdsmd-cell">{request.currentLevel || "New Agent"}</td>
                  <td className="mdsmd-cell">{request.requestedLevel}</td>
                  <td className="status-cell">
                    <span className={`status-badge status-${request.status}`}>
                      {request.status}
                    </span>
                  </td>
                  <td className="date-cell">
                    {request.createdAt?.toDate
                      ? request.createdAt.toDate().toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td className="actions-cell">
                    {request.status === "pending" ? (
                      <div className="action-buttons">
                        <button
                          onClick={() => handleApprove(request)}
                          className="charge-btn"
                          disabled={charging[request.id]}
                        >
                          {charging[request.id] ? "Processing..." : "Approve"}
                        </button>
                        <button
                          onClick={() => handleReject(request)}
                          className="reject-btn"
                          disabled={charging[request.id]}
                        >
                          Reject
                        </button>
                      </div>
                    ) : request.status === "approved" ? (
                      <span className="completed-text">✓ Approved</span>
                    ) : request.status === "rejected" ? (
                      <span className="rejected-text">Rejected</span>
                    ) : (
                      <span className="not-ready">Not Ready</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

